import PageHeader from './ui/PageHeader';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBreadcrumbJsonLd } from '../hooks/useBreadcrumbJsonLd';

const TermsOfService = () => {
    useDocumentMeta({
        title: 'Terms of Service | Antz AI',
        description: 'Antz AI terms of service — rules and guidelines governing the use of our AI-powered enterprise automation platform and services.',
    });
    useBreadcrumbJsonLd([
        { name: 'Home', href: '/' },
        { name: 'Terms of Service', href: '/terms-of-service' },
    ]);

    return (
        <div className="min-h-screen bg-background text-foreground pt-32 pb-20">
            <PageHeader
                prefix="TERMS OF"
                highlight="SERVICE"
                description="Please read these terms carefully."
            />

            <section className="container mx-auto px-4 max-w-4xl">
                <div className="prose prose-invert prose-lg max-w-none">
                    <p className="text-muted-foreground mb-8">
                        <strong>Last Updated:</strong> December 2024
                    </p>

                    <div className="space-y-8">
                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">1. Acceptance of Terms</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                By accessing or using Antz AI's services, website, and AI-powered enterprise automation solutions ("Services"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Services.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">2. Description of Services</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                Antz AI provides AI-powered enterprise automation solutions, including but not limited to intelligent agents for sales optimization, financial analysis, customer growth, supply chain management, and procurement. Our Services help businesses automate complex processes and derive actionable insights from their data.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">3. User Accounts</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">To access certain features, you may need to create an account. You agree to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                <li>Provide accurate and complete information</li>
                                <li>Maintain the security of your account credentials</li>
                                <li>Promptly notify us of any unauthorized access</li>
                                <li>Be responsible for all activities under your account</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">4. Acceptable Use</h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">You agree not to:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2">
                                <li>Use the Services for any unlawful purpose</li>
                                <li>Violate any applicable laws or regulations</li>
                                <li>Infringe upon intellectual property rights</li>
                                <li>Transmit malicious code or interfere with the Services</li>
                                <li>Attempt to gain unauthorized access to our systems</li>
                                <li>Use the Services to compete with Antz AI</li>
                                <li>Reverse engineer or decompile our software</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">5. Intellectual Property</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                All content, features, and functionality of our Services, including but not limited to software, algorithms, text, graphics, logos, and trademarks, are owned by Antz AI and protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written consent.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">6. Your Data</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                You retain ownership of any data you upload or process through our Services. By using our Services, you grant Antz AI a limited license to process your data solely for the purpose of providing the Services. We will handle your data in accordance with our Privacy Policy.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">7. Payment Terms</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you subscribe to paid Services, you agree to pay all applicable fees. Fees are non-refundable except as required by law or as expressly stated in your service agreement. We reserve the right to modify pricing with reasonable notice.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">8. Service Availability</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We strive to maintain high availability but do not guarantee uninterrupted access to our Services. We may modify, suspend, or discontinue any aspect of the Services at any time with reasonable notice when possible.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">9. Disclaimers</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. AI-GENERATED INSIGHTS AND RECOMMENDATIONS SHOULD BE VERIFIED BEFORE MAKING BUSINESS DECISIONS.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">10. Limitation of Liability</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ANTZ AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, REGARDLESS OF THE CAUSE OF ACTION.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">11. Indemnification</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                You agree to indemnify and hold harmless Antz AI and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Services or violation of these Terms.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">12. Termination</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We may terminate or suspend your access to the Services at any time for any reason, including violation of these Terms. Upon termination, your right to use the Services will immediately cease.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">13. Governing Law</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Hyderabad, India.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">14. Changes to Terms</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website. Your continued use of the Services after such changes constitutes acceptance of the new Terms.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold text-foreground mb-4">15. Contact Us</h2>
                            <p className="text-muted-foreground leading-relaxed">
                                If you have any questions about these Terms of Service, please contact us at:
                            </p>
                            <div className="mt-4 text-muted-foreground">
                                <p><strong>Email:</strong> info@antz.ai</p>
                                <p><strong>Location:</strong> Hyderabad, India</p>
                            </div>
                        </section>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default TermsOfService;
